import React, { FC, ReactElement, useRef } from 'react'

import loadable from '@loadable/component'
import { Link as RouterLink } from 'react-router-dom'

import {
  NavigationSlates,
  NetworkVisibility,
  RoleType,
} from '@tribeplatform/gql-client/types'
import { useAuthToken } from '@tribeplatform/react-sdk/hooks'
import {
  SlateRenderer,
  SlateRendererProps,
} from '@tribeplatform/react-slate-kit/components'
import {
  Frame,
  FrameProps,
  ResponsiveLogo,
} from '@tribeplatform/react-ui-kit/Frame'
import { useDocument } from '@tribeplatform/react-ui-kit/hooks'

import { useQuery } from '../../lib/useQuery'
import { useMobileSidebar } from '../../MobileSidebarProvider'
import { CookieContextProvider } from '../Apps/CookieConsent'
import { ErrorBoundary } from '../Error/ErrorBoundry'
import { Logo } from '../Logo'
import { NavigationBottom } from '../Navbar/NavigationBottom'
import { NavbarNavigation } from '../Navbar/navigations/NavbarNavigation'
import { useMobileBottomNavigation } from '../Navbar/useMobileBottomNavigation'
import { SquareLogo } from '../SquareLogo'
import { layoutsAdditionalContext } from './constants'
import { SlateLayout } from './layout.enum'
import { SidebarFooter } from './SidebarFooter'

const SampleContent = loadable(
  () => import('./sample-components/SampleContent.component'),
  {
    resolveComponent: m => m.SampleContent,
  },
)
const SampleHeader = loadable(
  () => import('./sample-components/SampleHeader.component'),
  {
    resolveComponent: m => m.SampleHeader,
  },
)
const SampleSidebar = loadable(
  () => import('./sample-components/SampleSidebar.component'),
  {
    resolveComponent: m => m.SampleSidebar,
  },
)

const LayoutQueryMap = {
  'no-sidebar': SlateLayout.CLASSIC,
  default: SlateLayout.DEFAULT,
  basic: SlateLayout.BASIC,
  'no-header': SlateLayout.NO_HEADER,
}

type LayoutProps = {
  navigationSlates: NavigationSlates
  context?: SlateRendererProps['context']
  navigations?: {
    Header: ReactElement
    Sidebar1: ReactElement
    Sidebar2: ReactElement
    Footer: ReactElement
  }
  layout: SlateLayout
  hideContent?: boolean
  hideNavigation?: boolean
  sticky?: FrameProps['sticky']
}

export const Layout: FC<LayoutProps> = ({
  layout,
  context,
  navigationSlates,
  navigations,
  hideContent,
  hideNavigation,
  sticky = 'top-bar-navigation',
  children,
}) => {
  const { document } = useDocument()
  const initialLayoutQuery = useRef<keyof typeof LayoutQueryMap>()
  const { mobileSidebarOpen, closeMobileSidebar, navigationItems } =
    useMobileSidebar()

  const { enabled: mobileBottomNavigationEnabled } = useMobileBottomNavigation()

  const queryParams = useQuery()

  if (!initialLayoutQuery.current && queryParams.layout) {
    initialLayoutQuery.current = queryParams.layout as any
  }

  const queryLayoutSlug =
    LayoutQueryMap[initialLayoutQuery.current || queryParams?.layout]

  const layoutSlug = (queryLayoutSlug || layout) as SlateLayout

  const extraContext = {
    slate: layoutsAdditionalContext[layoutSlug] || {},
    blocks: {},
  }
  const { header, sidebar1, sidebar2, footer } = navigationSlates || {}
  const { Header, Sidebar1 } = navigations || {
    Header: (
      <SlateRenderer
        slate={header}
        context={context}
        extraContext={extraContext}
      />
    ),
    Sidebar1: (
      <SlateRenderer
        slate={sidebar1}
        context={context}
        extraContext={extraContext}
      />
    ),
    Sidebar2: (
      <SlateRenderer
        slate={sidebar2}
        context={context}
        extraContext={extraContext}
      />
    ),
    Footer: (
      <SlateRenderer
        slate={footer}
        context={context}
        extraContext={extraContext}
      />
    ),
  }

  const { data: authToken } = useAuthToken()
  const { networkPublicInfo } = authToken || {}

  const guardNavigation =
    networkPublicInfo?.visibility === NetworkVisibility.private &&
    (!authToken?.member?.role?.type ||
      authToken?.member?.role?.type === RoleType.guest)

  const logo: ResponsiveLogo = {
    plain: (
      <RouterLink to={guardNavigation ? '#' : '/'} className="block">
        <Logo image={networkPublicInfo?.logo} alt={networkPublicInfo?.name} />
      </RouterLink>
    ),
    square: (
      <RouterLink to={guardNavigation ? '#' : '/'} className="block">
        <SquareLogo
          image={networkPublicInfo?.favicon}
          alt={networkPublicInfo?.name}
          size="sm"
        />
      </RouterLink>
    ),
  }

  const bottomBar = mobileBottomNavigationEnabled ? (
    <NavigationBottom />
  ) : undefined

  const topBar = (
    <ErrorBoundary>{hideNavigation ? <SampleHeader /> : Header}</ErrorBoundary>
  )

  const navigation = (
    <ErrorBoundary>
      {hideNavigation ? (
        <SampleSidebar />
      ) : (
        <>
          {Sidebar1}
          <CookieContextProvider>
            <SidebarFooter />
          </CookieContextProvider>
        </>
      )}
    </ErrorBoundary>
  )

  const navigationSecondaryMarkup = !guardNavigation &&
    navigationItems?.length && (
      <NavbarNavigation navigationItems={navigationItems} />
    )

  let frameLayout
  switch (layoutSlug) {
    case SlateLayout.CLASSIC:
      frameLayout = 'no-sidebar'
      break
    case SlateLayout.BASIC:
      frameLayout = 'basic'
      break
    case SlateLayout.NO_HEADER:
      frameLayout = 'no-header'
      break
    case SlateLayout.DEFAULT:
    default:
      frameLayout = 'default'
      break
  }

  let fullWidth
  switch (queryLayoutSlug) {
    case SlateLayout.CLASSIC:
    case SlateLayout.BASIC:
      fullWidth = true
      break
    case SlateLayout.NO_HEADER:
    case SlateLayout.DEFAULT:
    default:
      fullWidth = false
      break
  }

  return (
    <Frame
      document={document}
      logo={logo}
      topBar={topBar}
      navigation={navigation}
      navigationSecondary={navigationSecondaryMarkup}
      mobileSidebarOpen={mobileSidebarOpen}
      onNavigationDismiss={closeMobileSidebar}
      bottomBar={bottomBar}
      layout={frameLayout}
      fullWidth={fullWidth}
      sticky={sticky}
    >
      <ErrorBoundary>
        {hideContent ? <SampleContent /> : children}
      </ErrorBoundary>
    </Frame>
  )
}
